import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { HiOutlineMagnifyingGlass, HiOutlineTableCells } from "react-icons/hi2";

import { ColumnDefinition, HeaderDictionary } from "@/types/Table.type";
import { TableMetaProps } from "@/hooks/table/useTableConfig";
import Button from "@/components/atoms/Button";
import Checkbox from "@/components/atoms/Checkbox";
import SelectComboBox from "@/components/organisms/SelectComboBox";

type Props<T> = {
  columnIndex?: number;
  columnNames: HeaderDictionary;
  columnVisibility: Record<string, boolean>;
  toggleColumnVisibility: (column: ColumnDefinition, index?: number) => void;
  columns: ColumnDef<T>[];
};

const ConfigureTableColumnsButton = <T,>({
  columnIndex = 0,
  columnNames,
  columnVisibility,
  toggleColumnVisibility,
  columns,
}: Props<T>) => {
  const { t } = useTranslation();

  const options = Object.values(columnNames).slice(1);

  const getIcon = (option: ColumnDefinition) => {
    const column = columns.find((col) => col.id === option.id);
    const meta: TableMetaProps = column?.meta || {};
    if (meta?.heading?.icon) {
      return meta.heading.icon;
    }
    return null;
  };

  return (
    <Popover>
      <PopoverButton as={"div"}>
        <Button
          leadingIcon={<HiOutlineTableCells className="size-5" />}
          label={t("search.table.config")}
          className="!px-1.5 !py-1"
          color="natural"
          variants={"outline"}
          size="2xs"
        />
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: "bottom end", gap: "8px", offset: "13px" }}
        className="flex flex-col rounded-lg border border-secondary bg-white py-4"
      >
        <SelectComboBox
          leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
          options={options}
          className={"px-4"}
          placeholder={t("static.modalAdd.search")}
          renderOption={(option) => (
            <ToggleColumn
              key={option.id}
              option={option}
              icon={getIcon(option)}
              columnVisibility={columnVisibility}
              toggleColumnVisibility={toggleColumnVisibility}
              index={columnIndex}
            />
          )}
        />
      </PopoverPanel>
    </Popover>
  );
};

export default ConfigureTableColumnsButton;

type ToggleProps = {
  option: ColumnDefinition;
  index: number;
  columnVisibility: Record<string, boolean>;
  toggleColumnVisibility: (column: ColumnDefinition, index?: number) => void;
  icon?: IconType | null;
};

const ToggleColumn = ({ option, icon: Icon, index, columnVisibility, toggleColumnVisibility }: ToggleProps) => {
  const isSelected = columnVisibility[option.id];

  return (
    <label
      key={option.id}
      className={
        "group flex cursor-pointer select-none items-center gap-3 rounded px-4 py-1.5 font-medium text-primary hover:bg-secondary-hover"
      }
    >
      {Icon && <Icon className="size-5 shrink-0 stroke-2 text-quaternary" />}

      <div className={"grow truncate text-sm font-medium text-secondary"}>{option.name}</div>

      <div className={"shrink-0 pl-2"}>
        <Checkbox
          name={option.id}
          sizing={"md"}
          checked={isSelected}
          onChange={() => toggleColumnVisibility(option, index)}
        />
      </div>
    </label>
  );
};
